'use client'

import { useId } from 'react'
import { Controller } from 'react-hook-form'

import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue
} from '@/_components/ui/select'
import { cn } from '@/_lib/utils'

import { MFieldError } from './m-field-error'

import type { Control, FieldPath, FieldValues, RegisterOptions } from 'react-hook-form'

export type MSelectOption = { id: string; name: string }

const boomexSelectTriggerClass =
	'!relative !flex !h-auto !min-h-[58px] !w-full !items-center !text-left ' +
	'!rounded-[10px] !border !border-solid !border-medante-purple-dark !bg-medante-background ' +
	'!pt-[23px] !pb-[14px] !pl-[25px] !pr-10 ' +
	'!text-sm !font-semibold !leading-normal !text-medante-purple-dark !shadow-none focus:!shadow-none focus-visible:!shadow-none ' +
	'!outline-none !ring-0 !ring-offset-0 focus:!outline-none focus:!ring-0 focus:!ring-offset-0 ' +
	'focus-visible:!outline-none focus-visible:!ring-0 focus-visible:!ring-offset-0 ' +
	'data-[state=open]:!ring-0 data-[state=open]:!ring-offset-0 aria-invalid:!ring-0 ' +
	'data-[placeholder]:!text-transparent md:!rounded-[14px] ' +
	'[&_[data-slot=select-value]]:!min-w-0 [&_[data-slot=select-value]]:!flex-1 [&_[data-slot=select-value]]:!text-left ' +
	'[&>svg:last-child]:!pointer-events-none [&>svg:last-child]:!absolute [&>svg:last-child]:!right-3 [&>svg:last-child]:!top-1/2 [&>svg:last-child]:!m-0 [&>svg:last-child]:!size-[18px] [&>svg:last-child]:!-translate-y-1/2 [&>svg:last-child]:!shrink-0 [&>svg:last-child]:!text-medante-purple-dark [&>svg:last-child]:!opacity-100'

const boomexSelectContentClass =
	'!border !border-solid !border-medante-purple-dark !bg-medante-background !p-1 !shadow-md '

const selectItemClass =
	'!rounded-lg !px-2.5 !py-1 !pl-2.5 !pr-6 text-sm font-medium text-medante-purple-dark outline-none ring-0 hover:bg-medante-purple-dark/10 focus:bg-medante-purple-dark focus:text-white focus:outline-none focus:ring-0 data-[highlighted]:outline-none data-[highlighted]:ring-0'

const selectFloatLabel =
	'pointer-events-none absolute left-[25px] top-[19px] z-10 max-w-[calc(100%-2.75rem)] origin-top-left truncate text-sm font-normal transition-transform duration-200 ease-out max-md:top-[15px] max-md:text-[13px] ' +
	'group-focus-within:-translate-y-[10px] group-focus-within:scale-90 ' +
	'group-data-[has-value]:-translate-y-[10px] group-data-[has-value]:scale-90'

type Props<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = {
	name: TName
	control: Control<TFieldValues>
	rules?: RegisterOptions<TFieldValues, TName>
	label: string
	required?: boolean
	error?: string
	options: MSelectOption[]
	triggerClassName?: string
	contentClassName?: string
}

export function MSelect<
	TFieldValues extends FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
	name,
	control,
	rules,
	label,
	required,
	error,
	options,
	triggerClassName = boomexSelectTriggerClass,
	contentClassName = boomexSelectContentClass
}: Props<TFieldValues, TName>) {
	const invalid = Boolean(error)
	const triggerId = useId()

	return (
		<div>
			<Controller
				name={name}
				control={control}
				rules={rules}
				render={({ field }) => (
					<div className='group relative' data-has-value={field.value ? '' : undefined}>
						<Select
							name={String(name)}
							value={field.value}
							onValueChange={v => {
								field.onChange(v)
								field.onBlur()
							}}
						>
							<SelectTrigger
								id={triggerId}
								name={String(name)}
								aria-label={label}
								className={cn(
									triggerClassName,
									invalid &&
										'!border-[#fe2b20] !text-[#fe2b20] data-[placeholder]:!text-transparent [&>svg:last-child]:!text-[#fe2b20]'
								)}
							>
								<SelectValue placeholder=' ' aria-label='Select value' />
							</SelectTrigger>
							<SelectContent className={cn('bg2 px-3 py-2', contentClassName)}>
								{options.map(option => (
									<SelectItem
										key={option.id}
										value={option.id}
										className={selectItemClass}
									>
										{option.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<label
							htmlFor={triggerId}
							className={cn(
								selectFloatLabel,
								invalid ? 'text-[#fe2b20]' : 'text-medante-purple-dark'
							)}
						>
							{label}
							{required && <span className='text-[#fe2b20]'>*</span>}
						</label>
					</div>
				)}
			/>
			<MFieldError message={error} />
		</div>
	)
}
